import type { LabelConfig, VariantSettings } from '../types';

// Which icon / font / sticker type / theme an order used, as keys for the usage counters
// (see stats.ts). Derived from the stickers that are actually printed, so that what the
// statistics say matches what the customer ordered:
//  - only visible stickers with a count above 0 are looked at,
//  - the icon and font are the ones the sticker really uses (its own, else the sheet-wide one),
//  - an empty theme means "inherit the sheet's theme" (this is what the wizard and "add sticker" leave),
//  - every value counts once per order, however many stickers use it.

export interface OrderStatKeys {
  icons: string[];
  fonts: string[];
  variants: string[];
  themes: string[];
}

// Firestore field names must be non-empty, must not look like __x__ and must not contain "/" — and this
// also keeps free-form values (font names, ids) from turning into odd nested paths.
function fieldKey(raw: string | undefined | null): string | null {
  const key = (raw ?? '').toString().trim().replace(/[/.[\]*`~]/g, '_').slice(0, 100);
  if (!key || /^__.*__$/.test(key)) return null;
  return key;
}

// Sticker types added by a customer ("custom-<time>") or by the admin ("template-<time>") carry a
// timestamp in their id: one counter per type, not one per id, or the document grows forever.
function variantKey(variantId: string): string {
  return variantId.replace(/^custom-\d+$/, 'custom').replace(/^template-\d+$/, 'template');
}

// Library icons have stable ids worth counting one by one. Anything else (an uploaded or AI-generated
// icon that only exists in this customer's session) is collapsed. Without a known-id set only the
// obviously session-only "custom-<time>" ids can be recognised.
function iconKey(iconId: string | undefined, known?: ReadonlySet<string>): string | null {
  if (!iconId) return null;
  if (known && !known.has(iconId)) return iconId.startsWith('ai-') ? 'ai-generated' : 'custom-upload';
  if (/^custom-\d+$/.test(iconId)) return 'custom-upload';
  return iconId;
}

function themeKey(s: VariantSettings, config: LabelConfig): string | null {
  if (s.backgroundId) return 'background-image';
  if (s.customColor) return 'custom-color';
  return s.themeId || config.themeId || (config.customColor ? 'custom-color' : null);
}

export function collectOrderStatKeys(config: LabelConfig, knownIconIds?: ReadonlySet<string>): OrderStatKeys {
  const icons = new Set<string>();
  const fonts = new Set<string>();
  const variants = new Set<string>();
  const themes = new Set<string>();
  const add = (set: Set<string>, raw: string | undefined | null) => { const key = fieldKey(raw); if (key) set.add(key); };

  for (const [variantId, s] of Object.entries(config.variantSettings)) {
    if (s.visible === false || (s.customCount ?? 0) <= 0) continue;   // not printed
    add(variants, variantKey(variantId));
    if (s.isFullImage && !s.templateId) continue;                     // an uploaded picture: no icon, theme or text styling to speak of
    add(icons, iconKey(s.customIcon?.id ?? config.icon?.id, knownIconIds));
    add(fonts, s.fontFamily || config.fontFamily || 'Arial');
    add(themes, themeKey(s, config));
  }
  return { icons: [...icons], fonts: [...fonts], variants: [...variants], themes: [...themes] };
}
